import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { ModalContainer, ModalBackdrop } from '../styles'
import { useDynamicTheme } from '../../../Hooks'

type SearchModalProps = {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
}

export const SearchModal = memo<SearchModalProps>(({ isOpen, onClose, children }) => {
  const modalRef = useRef<HTMLDivElement>(null)
  const { isMobile } = useDynamicTheme()
  const [maxHeight, setMaxHeight] = useState<string>('80vh')
  const [mobileTop, setMobileTop] = useState<string>('80px')

  const calculatePosition = useCallback(() => {
    if (!modalRef.current || !isMobile) return

    const searchInput = document.querySelector(
      'input[type="search"], input[placeholder*="Search"]'
    ) as HTMLElement
    if (!searchInput) return

    let searchWrapper = searchInput.parentElement
    while (searchWrapper && searchWrapper !== document.body) {
      const computedStyle = window.getComputedStyle(searchWrapper)
      if (
        computedStyle.position === 'relative' ||
        searchWrapper.classList.toString().includes('SearchWrapper')
      ) {
        const wrapperRect = searchWrapper.getBoundingClientRect()
        const wrapperBottom = wrapperRect.bottom
        const topPosition = wrapperBottom
        setMobileTop(`${Math.max(topPosition, 80)}px`)
        return
      }
      searchWrapper = searchWrapper.parentElement
    }

    const inputRect = searchInput.getBoundingClientRect()
    const inputBottom = inputRect.bottom
    setMobileTop(`${Math.max(inputBottom, 80)}px`)
  }, [isMobile])

  const calculateMaxHeight = useCallback(() => {
    if (!modalRef.current) return

    const rect = modalRef.current.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const modalTop = rect.top
    const bottomPadding = 8
    const availableSpace = viewportHeight - modalTop - bottomPadding
    const minHeight = 200

    if (isMobile) {
      const topValue = parseFloat(mobileTop) || 80
      const mobileMaxHeight = viewportHeight - topValue - bottomPadding
      setMaxHeight(`${Math.max(mobileMaxHeight, minHeight)}px`)
    } else if (availableSpace > minHeight) {
      setMaxHeight(`${availableSpace}px`)
    } else {
      setMaxHeight(`${minHeight}px`)
    }
  }, [isMobile, mobileTop])

  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.width = '100%'
      document.body.style.overflow = 'hidden'

      return () => {
        document.body.style.position = ''
        document.body.style.top = ''
        document.body.style.width = ''
        document.body.style.overflow = ''
        window.scrollTo(0, scrollY)
      }
    }
    return undefined
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    requestAnimationFrame(() => {
      calculatePosition()
      calculateMaxHeight()
    })

    const handleResize = () => {
      calculatePosition()
      calculateMaxHeight()
    }

    const handleScroll = () => {
      calculatePosition()
      calculateMaxHeight()
    }

    window.addEventListener('resize', handleResize)
    window.addEventListener('scroll', handleScroll, { capture: true, passive: true })

    const resizeObserver = new ResizeObserver(() => {
      calculatePosition()
      calculateMaxHeight()
    })

    if (modalRef.current) {
      resizeObserver.observe(modalRef.current)
    }

    const searchInput = document.querySelector(
      'input[type="search"], input[placeholder*="Search"]'
    ) as HTMLElement
    if (searchInput && isMobile) {
      let searchWrapper = searchInput.parentElement
      while (searchWrapper && searchWrapper !== document.body) {
        const computedStyle = window.getComputedStyle(searchWrapper)
        if (computedStyle.position === 'relative') {
          resizeObserver.observe(searchWrapper)
          break
        }
        searchWrapper = searchWrapper.parentElement
      }
      if (!searchWrapper) {
        resizeObserver.observe(searchInput)
      }
    }

    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('scroll', handleScroll, { capture: true } as EventListenerOptions)
      resizeObserver.disconnect()
    }
  }, [isOpen, calculatePosition, calculateMaxHeight, isMobile])

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent): void => {
      const modalContainer = modalRef.current
      if (modalContainer && modalContainer.contains(e.target as Node)) {
        return
      }
      if (e.target === e.currentTarget) {
        onClose()
      }
    },
    [onClose]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose]
  )

  const handleModalMouseDown = useCallback((e: React.MouseEvent): void => {
    e.stopPropagation()
  }, [])

  const handleModalClick = useCallback((e: React.MouseEvent): void => {
    e.stopPropagation()
  }, [])

  if (!isOpen) return null

  return (
    <>
      <ModalBackdrop onClick={handleBackdropClick} aria-hidden="true" />
      <ModalContainer
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label="Search results"
        onKeyDown={handleKeyDown}
        onMouseDown={handleModalMouseDown}
        onClick={handleModalClick}
        $maxHeight={maxHeight}
        $mobileTop={isMobile ? mobileTop : undefined}
      >
        {children}
      </ModalContainer>
    </>
  )
})

SearchModal.displayName = 'SearchModal'
